import React, { useMemo } from 'react'
import messages from '../../shared/messages'
import { ReactComponent as MyProjects } from '@mike/mike-shared-frontend/media/icons/MyProjects'
import { ReactComponent as TableResults } from '@mike/mike-shared-frontend/media/icons/TableResults'
import { ReactComponent as Users } from '@mike/mike-shared-frontend/media/icons/Users'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { Tabs } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { LinkTab, ActiveLabel, activeSvgStyle } from '../AppNav'
import { useTypedSelector } from '../../reducers'
import { canManageSystemStatus } from '../../utils/systemStatusAccess'

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    height: 3
  },
  // Let the tab row size to its content and keep every label on one line so
  // the tabs are never clipped or scrolled.
  '& .MuiTab-root': {
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    paddingLeft: 16,
    paddingRight: 16
  }
})

interface IProps {
  showSiteAccess?: boolean
}

const IAMNav = (props: IProps) => {
  const { showSiteAccess } = props
  const intl = useIntl()
  const isBackOffice = useTypedSelector(state => state.admin.isBackOffice)
  const showSystemStatus = canManageSystemStatus(isBackOffice)

  function usePath() {
    return useLocation().pathname
  }

  const currentPath = usePath()

  // Tabs are keyed by name; `false` is MUI's "no tab selected". A path whose
  // tab is not rendered resolves to `false` too, since Tabs rejects a value no
  // tab carries.
  const activeTab = useMemo(() => {
    if (currentPath.startsWith('/backoffice/system-status')) {
      return showSystemStatus ? 'systemStatus' : false
    }

    if (currentPath.startsWith('/site-access')) {
      return showSiteAccess ? 'siteAccess' : false
    }

    if (currentPath.startsWith('/site-info')) {
      return showSiteAccess ? 'siteInfo' : false
    }

    if (
      currentPath.startsWith('/site') ||
      currentPath.startsWith('/project') ||
      currentPath.startsWith('/recycle-bin')
    ) {
      return 'data'
    }

    return false
  }, [currentPath, showSiteAccess, showSystemStatus])

  return (
    <StyledTabs
      value={activeTab}
      indicatorColor="secondary"
      textColor="primary"
    >
      <LinkTab
        value="data"
        to={'/site'}
        label={
          activeTab === 'data' ? (
            <ActiveLabel>{intl.formatMessage(messages.data)}</ActiveLabel>
          ) : (
            <span>{intl.formatMessage(messages.data)}</span>
          )
        }
        icon={
          <MyProjects
            style={activeTab === 'data' ? activeSvgStyle : undefined}
          />
        }
      />
      {showSiteAccess ? (
        <LinkTab
          value="siteAccess"
          to={'/site-access'}
          label={
            activeTab === 'siteAccess' ? (
              <ActiveLabel>
                {intl.formatMessage(messages.tenantAccess)}
              </ActiveLabel>
            ) : (
              <span>{intl.formatMessage(messages.tenantAccess)}</span>
            )
          }
          icon={
            <Users
              style={activeTab === 'siteAccess' ? activeSvgStyle : undefined}
            />
          }
        />
      ) : null}
      {showSiteAccess ? (
        <LinkTab
          value="siteInfo"
          to={'/site-info'}
          label={
            activeTab === 'siteInfo' ? (
              <ActiveLabel>
                {intl.formatMessage(messages.tenantInfo)}
              </ActiveLabel>
            ) : (
              <span>{intl.formatMessage(messages.tenantInfo)}</span>
            )
          }
          icon={
            <TableResults
              style={activeTab === 'siteInfo' ? activeSvgStyle : undefined}
            />
          }
        />
      ) : null}
      {showSystemStatus ? (
        <LinkTab
          value="systemStatus"
          to={'/backoffice/system-status'}
          label={
            activeTab === 'systemStatus' ? (
              <ActiveLabel>
                {intl.formatMessage({ id: 'systemStatus.nav.label' })}
              </ActiveLabel>
            ) : (
              <span>
                {intl.formatMessage({ id: 'systemStatus.nav.label' })}
              </span>
            )
          }
          icon={
            <NotificationsNoneIcon
              sx={{
                // match the other tab icons' colour (BrandBlue base, same
                // active filter)
                color: 'var(--BrandBlue--default, #0288D1)',
                ...(activeTab === 'systemStatus' ? activeSvgStyle : {})
              }}
            />
          }
        />
      ) : null}
    </StyledTabs>
  )
}

export default IAMNav
